/**
 * Display status calculation utilities
 * Determines real-time on/off status based on schedule and timezone
 */

import { 
  DisplayStatus, 
  DisplaySchedule, 
  LegacySchedule, 
  EnhancedSchedule,
  DisplayStatusType 
} from '@/types/display';
import { 
  convertToTimezone, 
  formatDateForSchedule, 
  getDayOfWeek 
} from './timezoneUtils';

/**
 * Checks if current time is within operating hours
 */
function isTimeInRange(
  currentTime: Date, 
  startTime: string, 
  endTime: string
): { isOpen: boolean; minutesUntilChange: number } {
  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();
  const currentTotalMinutes = currentHour * 60 + currentMinute;
  
  // Parse start and end times
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startTotalMinutes = startHour * 60 + startMin;
  const endTotalMinutes = endHour * 60 + endMin;
  
  // Handle overnight hours (e.g., 22:00 - 02:00)
  const isOvernight = endTotalMinutes < startTotalMinutes;
  
  let isOpen: boolean;
  let minutesUntilChange: number;
  
  if (isOvernight) {
    // Overnight schedule
    isOpen = currentTotalMinutes >= startTotalMinutes || currentTotalMinutes < endTotalMinutes;
    
    if (isOpen) {
      // Currently open - calculate time until closing
      if (currentTotalMinutes >= startTotalMinutes) {
        // Before midnight
        minutesUntilChange = (24 * 60) - currentTotalMinutes + endTotalMinutes;
      } else {
        // After midnight
        minutesUntilChange = endTotalMinutes - currentTotalMinutes;
      }
    } else {
      // Currently closed - calculate time until opening
      if (currentTotalMinutes < startTotalMinutes) {
        minutesUntilChange = startTotalMinutes - currentTotalMinutes;
      } else {
        // This shouldn't happen in overnight mode, but handle gracefully
        minutesUntilChange = (24 * 60) - currentTotalMinutes + startTotalMinutes;
      }
    }
  } else {
    // Normal same-day schedule
    isOpen = currentTotalMinutes >= startTotalMinutes && currentTotalMinutes < endTotalMinutes;
    
    if (isOpen) {
      // Currently open - time until closing
      minutesUntilChange = endTotalMinutes - currentTotalMinutes;
    } else {
      // Currently closed - time until opening
      if (currentTotalMinutes < startTotalMinutes) {
        minutesUntilChange = startTotalMinutes - currentTotalMinutes;
      } else {
        // After closing time - opening is tomorrow
        minutesUntilChange = (24 * 60) - currentTotalMinutes + startTotalMinutes;
      }
    }
  }
  
  return { isOpen, minutesUntilChange };
}

/**
 * Checks if current date is within season date range
 */
function isWithinSeason(
  currentDate: Date, 
  seasonStart?: string, 
  seasonEnd?: string
): boolean {
  if (!seasonStart || !seasonEnd) {
    return true; // No season restrictions
  }
  
  const current = new Date(currentDate);
  const start = new Date(seasonStart);
  const end = new Date(seasonEnd);
  
  // Handle season spanning year boundary (e.g., Nov 24 - Jan 6)
  if (start.getTime() > end.getTime()) {
    // Season spans New Year
    const currentYear = current.getFullYear();
    const nextYearEnd = new Date(end);
    nextYearEnd.setFullYear(currentYear + 1);
    
    return current >= start || current <= nextYearEnd;
  } else {
    // Normal season within same year
    return current >= start && current <= end;
  }
}

/**
 * Gets the hours for a specific day from enhanced schedule
 */
function getHoursForDay(
  schedule: EnhancedSchedule,
  dayOfWeek: string,
  dateStr: string
): { start: string; end: string } | null {
  // Check date overrides first
  if (schedule.date_overrides && schedule.date_overrides[dateStr] !== undefined) {
    return schedule.date_overrides[dateStr];
  }
  
  // Fall back to regular hours
  return schedule.regular_hours[dayOfWeek] || null;
}

/**
 * Converts legacy schedule to check current status
 */
function getStatusFromLegacySchedule(
  schedule: LegacySchedule,
  timezone: string,
  currentTime?: Date
): DisplayStatus {
  const now = currentTime || new Date();
  const localTime = convertToTimezone(now, timezone);
  const currentDateStr = formatDateForSchedule(localTime);
  const dayOfWeek = getDayOfWeek(localTime);
  
  // Check if current day is in scheduled days
  const isScheduledDay = schedule.days.includes(dayOfWeek) || 
                        schedule.days.includes(dayOfWeek.substring(0, 3)) ||
                        schedule.days.some(d => d.toLowerCase().startsWith(dayOfWeek.substring(0, 2)));
  
  if (!isScheduledDay) {
    return {
      status: 'off',
      reason: 'not-scheduled-today',
      message: 'Not scheduled for today',
      currentTime: localTime.toISOString(),
      timezone
    };
  }
  
  // Check season dates
  const isInSeason = isWithinSeason(
    localTime, 
    schedule.start_date, 
    schedule.end_date
  );
  
  if (!isInSeason) {
    return {
      status: 'off-season',
      seasonStart: schedule.start_date,
      seasonEnd: schedule.end_date,
      message: `Display runs ${schedule.start_date} - ${schedule.end_date}`,
      currentTime: localTime.toISOString(),
      timezone
    };
  }
  
  // Check if currently within operating hours
  const { isOpen, minutesUntilChange } = isTimeInRange(
    localTime,
    schedule.hours.start,
    schedule.hours.end
  );
  
  return {
    status: isOpen ? 'on' : 'off',
    minutesUntilChange,
    nextChange: isOpen ? 'closing' : 'opening',
    todayHours: schedule.hours,
    message: isOpen 
      ? `Lights are on - ${formatMinutesUntilChange(minutesUntilChange, 'closing')}`
      : `Lights are off - ${formatMinutesUntilChange(minutesUntilChange, 'opening')}`,
    currentTime: localTime.toISOString(),
    timezone
  };
}

/**
 * Gets status from enhanced schedule format
 */
function getStatusFromEnhancedSchedule(
  schedule: EnhancedSchedule,
  currentTime?: Date
): DisplayStatus {
  const now = currentTime || new Date();
  const localTime = convertToTimezone(now, schedule.timezone);
  const currentDateStr = formatDateForSchedule(localTime);
  const dayOfWeek = getDayOfWeek(localTime);
  
  // Check season dates first
  if (schedule.season_dates) {
    const isInSeason = isWithinSeason(
      localTime,
      schedule.season_dates.start,
      schedule.season_dates.end
    );
    
    if (!isInSeason) {
      const nextSeason = getNextSeasonMessage(schedule.season_dates, localTime);
      return {
        status: 'off-season',
        seasonStart: schedule.season_dates.start,
        seasonEnd: schedule.season_dates.end,
        message: nextSeason,
        currentTime: localTime.toISOString(),
        timezone: schedule.timezone
      };
    }
  }
  
  // Get hours for today
  const todayHours = getHoursForDay(schedule, dayOfWeek, currentDateStr);
  
  if (!todayHours) {
    return {
      status: 'off',
      reason: 'no-hours-today',
      message: 'Closed today',
      currentTime: localTime.toISOString(),
      timezone: schedule.timezone
    };
  }
  
  // Check if currently within operating hours
  const { isOpen, minutesUntilChange } = isTimeInRange(
    localTime,
    todayHours.start,
    todayHours.end
  );
  
  return {
    status: isOpen ? 'on' : 'off',
    minutesUntilChange,
    nextChange: isOpen ? 'closing' : 'opening',
    todayHours,
    message: isOpen 
      ? `Lights are on - ${formatMinutesUntilChange(minutesUntilChange, 'closing')}`
      : `Lights are off - ${formatMinutesUntilChange(minutesUntilChange, 'opening')}`,
    currentTime: localTime.toISOString(),
    timezone: schedule.timezone
  };
}

/**
 * Formats minutes until change into human-readable string
 */
function formatMinutesUntilChange(minutes: number, action: 'opening' | 'closing'): string {
  if (minutes < 60) {
    return `${action} in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (minutes < 1440) { // Less than 24 hours
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${action} in ${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${action} in ${hours}h ${remainingMinutes}m`;
    }
  } else {
    const days = Math.floor(minutes / 1440);
    return `${action} in ${days} day${days !== 1 ? 's' : ''}`;
  }
}

/**
 * Gets next season message for off-season displays
 */
function getNextSeasonMessage(
  seasonDates: { start: string; end: string },
  currentDate: Date
): string {
  const currentYear = currentDate.getFullYear();
  const seasonStart = new Date(seasonDates.start);
  const seasonEnd = new Date(seasonDates.end);
  
  // If current date is before this year's season
  if (currentDate < seasonStart) {
    const startDate = seasonStart.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric' 
    });
    return `Display returns ${startDate}`;
  } else {
    // Current date is after season end, show next year
    const nextYearStart = new Date(seasonStart);
    nextYearStart.setFullYear(currentYear + 1);
    const startDate = nextYearStart.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
    return `Display returns ${startDate}`;
  }
}

/**
 * Checks if schedule format is enhanced or legacy
 */
function isEnhancedSchedule(schedule: DisplaySchedule): schedule is EnhancedSchedule {
  return schedule !== null && 'timezone' in schedule && 'regular_hours' in schedule;
}

/**
 * Main function to get display status
 */
export function getDisplayStatus(
  schedule: DisplaySchedule,
  fallbackTimezone?: string,
  currentTime?: Date
): DisplayStatus {
  // Handle null/undefined schedule
  if (!schedule) {
    return {
      status: 'no-schedule',
      message: 'No schedule posted. Lights may not be on this season.',
      currentTime: (currentTime || new Date()).toISOString(),
      timezone: fallbackTimezone || 'America/New_York'
    };
  }
  
  try {
    if (isEnhancedSchedule(schedule)) {
      return getStatusFromEnhancedSchedule(schedule, currentTime);
    } else {
      // Legacy schedule
      const timezone = fallbackTimezone || 'America/New_York';
      return getStatusFromLegacySchedule(schedule, timezone, currentTime);
    }
  } catch (error) {
    console.error('Error calculating display status:', error);
    
    // Attempt graceful fallback with basic info
    const safeTimezone = fallbackTimezone || 'America/New_York';
    const safeCurrentTime = currentTime || new Date();
    
    // Try to extract any basic info from the schedule for fallback
    let fallbackMessage = 'Unable to determine schedule';
    
    if (isEnhancedSchedule(schedule) && schedule.original_hours) {
      fallbackMessage = `Schedule format issue. Original hours: "${schedule.original_hours}"`;
    } else if ('hours' in schedule && schedule.hours) {
      fallbackMessage = `Schedule calculation error. Approximate hours: ${schedule.hours.start || 'unknown'} - ${schedule.hours.end || 'unknown'}`;
    }
    
    return {
      status: 'no-schedule',
      message: fallbackMessage,
      currentTime: safeCurrentTime.toISOString(),
      timezone: safeTimezone,
      reason: 'calculation-error'
    };
  }
}

/**
 * Gets a formatted status message for display
 */
export function getStatusMessage(status: DisplayStatus): string {
  if (status.message) {
    return status.message;
  }
  
  switch (status.status) {
    case 'on':
      return 'Lights are on';
    case 'off':
      return 'Lights are off';
    case 'no-schedule':
      return 'No schedule posted';
    case 'off-season':
      return 'Off season';
    default:
      return 'Status unknown';
  }
}

/**
 * Gets appropriate emoji for status
 */
export function getStatusEmoji(status: DisplayStatusType): string {
  switch (status) {
    case 'on':
      return '🟢';
    case 'off':
      return '🔴';
    case 'no-schedule':
      return '⚠️';
    case 'off-season':
      return '🟣';
    default:
      return '❓';
  }
}

/**
 * Gets CSS color class for status
 */
export function getStatusColor(status: DisplayStatusType): string {
  switch (status) {
    case 'on':
      return 'text-green-500';
    case 'off':
      return 'text-red-500';
    case 'no-schedule':
      return 'text-amber-500';
    case 'off-season':
      return 'text-violet-500';
    default:
      return 'text-gray-500';
  }
}

/**
 * Gets background color for status badge
 */
export function getStatusBgColor(status: DisplayStatusType): string {
  switch (status) {
    case 'on':
      return 'bg-green-100 border-green-200';
    case 'off':
      return 'bg-red-100 border-red-200';
    case 'no-schedule':
      return 'bg-amber-100 border-amber-200';
    case 'off-season':
      return 'bg-violet-100 border-violet-200';
    default:
      return 'bg-gray-100 border-gray-200';
  }
}